import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import api from '../api';

// ILS monetary amounts come from server already in shekels (fixIlsPnL); display as whole shekels
const fmt = (n, currency = 'USD') => {
  if (n === undefined || n === null || isNaN(n)) return '-';
  if (currency === 'ILS') {
    try {
      return new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Math.round(n));
    } catch { return `₪${Math.round(n).toLocaleString('he-IL')}`; }
  }
  try {
    return new Intl.NumberFormat('he-IL', { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
  } catch { return `${n.toFixed(2)} ${currency}`; }
};
// ILS per-share prices are in agorot — show as integer agorot
const fmtPrice = (n, currency = 'USD') => {
  if (n === undefined || n === null || isNaN(n)) return '-';
  if (currency === 'ILS') return `${Math.round(n).toLocaleString('he-IL')} אג'`;
  try {
    return new Intl.NumberFormat('he-IL', { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
  } catch { return `${n.toFixed(2)} ${currency}`; }
};
const fmtPct = (n) => (!n && n !== 0) ? '-' : (n >= 0 ? `+${n.toFixed(2)}%` : `${n.toFixed(2)}%`);
const fmtLive = (n, currency) => n === undefined ? null : (n >= 0 ? '+' : '') + fmt(n, currency);

function NewInstanceModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ stock_symbol: '', platform_name: '', base_currency: 'USD', notes: '', ticker_symbol: '', quote_source: 'finnhub', leverage_factor: 1 });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try { await api.post('/instances', form); onCreated(); }
    catch (e) { setError(e.response?.data?.error || 'שגיאה ביצירת פוזיציה'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <span className="modal-handle" />
        <div className="modal-title">פוזיציה חדשה</div>
        {error && <div className="error-msg">{error}</div>}
        <form onSubmit={submit}>
          <div className="form-group">
            <label className="form-label">שם המניה / נכס *</label>
            <input className="form-input" placeholder="לדוגמה: Apple Inc" value={form.stock_symbol}
              onChange={e => setForm({...form, stock_symbol: e.target.value})} required />
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">סימול (Ticker) *</label>
              <input className="form-input" placeholder="AAPL" value={form.ticker_symbol}
                onChange={e => setForm({...form, ticker_symbol: e.target.value.toUpperCase()})} required />
            </div>
            <div className="form-group">
              <label className="form-label">פלטפורמה *</label>
              <input className="form-input" placeholder="eToro" value={form.platform_name}
                onChange={e => setForm({...form, platform_name: e.target.value})} required />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">מטבע</label>
            <select className="form-select" value={form.base_currency} onChange={e => setForm({...form, base_currency: e.target.value})}>
              <option value="USD">דולר (USD)</option>
              <option value="ILS">שקל (ILS)</option>
              <option value="EUR">אירו (EUR)</option>
              <option value="GBP">פאונד (GBP)</option>
            </select>
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">מקור נתונים</label>
              <select className="form-select" value={form.quote_source} onChange={e => setForm({...form, quote_source: e.target.value})}>
                <option value="finnhub">Finnhub (מניות US)</option>
                <option value="yahoo">Yahoo Finance (TASE / מדדים)</option>
                <option value="bizportal">Bizportal (קרן TASE — טיקר=מס' נייר)</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">מינוף (x)</label>
              <input className="form-input" type="number" min="1" step="0.5" value={form.leverage_factor}
                onChange={e => setForm({...form, leverage_factor: parseFloat(e.target.value) || 1})} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">הערות</label>
            <input className="form-input" placeholder="אופציונלי" value={form.notes}
              onChange={e => setForm({...form, notes: e.target.value})} />
          </div>
          <div className="modal-actions">
            <button type="submit" className="btn btn-primary" style={{flex:1}} disabled={loading}>
              {loading ? 'יוצר...' : 'פתח פוזיציה'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>ביטול</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function LivePortfolioSummary({ instances, quotesMap, quotesLoading }) {
  const openWithTicker = instances.filter(i => i.status === 'open' && i.ticker_symbol && i.pnl?.remainingQty > 0);

  const liveData = useMemo(() => {
    if (!openWithTicker.length) return null;
    if (!quotesMap || Object.keys(quotesMap).length === 0) return null;
    let totalDailyChangeUSD = 0, totalDailyChangeILS = 0;
    let totalUnrealizedUSD = 0, totalUnrealizedILS = 0;
    let totalValueUSD = 0, totalValueILS = 0;
    let countedUSD = 0, countedILS = 0;
    openWithTicker.forEach(inst => {
      const quote = quotesMap[inst.id];
      if (!quote) return;
      const pnl = inst.pnl || {};
      const qty = pnl.remainingQty || 0;
      const avgBuy = pnl.avgBuyPrice || 0;
      const currentPrice = quote.current;
      const prevClose = quote.previous_close;
      if (!currentPrice || !qty) return;
      const currentValue = currentPrice * qty;
      const prevValue = prevClose * qty;
      const dailyChange = currentValue - prevValue;
      const unrealized = (currentPrice - avgBuy) * qty;
      if (inst.base_currency === 'ILS') {
        // quote prices are in agorot (×100 from Yahoo); convert to shekels before accumulating
        totalDailyChangeILS += dailyChange / 100; totalUnrealizedILS += unrealized / 100;
        totalValueILS += currentValue / 100; countedILS++;
      } else {
        totalDailyChangeUSD += dailyChange; totalUnrealizedUSD += unrealized;
        totalValueUSD += currentValue; countedUSD++;
      }
    });
    if (countedUSD === 0 && countedILS === 0) return null;
    return { totalDailyChangeUSD, totalDailyChangeILS, totalUnrealizedUSD, totalUnrealizedILS, totalValueUSD, totalValueILS, countedUSD, countedILS };
  }, [quotesMap]); // eslint-disable-line

  if (!openWithTicker.length) return null;

  const hasUSD = liveData?.countedUSD > 0;
  const hasILS = liveData?.countedILS > 0;

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: '.78rem', fontWeight: 700, color: 'var(--muted)', marginBottom: 8, letterSpacing: '.05em', textTransform: 'uppercase' }}>
        שוק חי · ברוטו
      </div>
      {quotesLoading && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {[1,2].map(k => (
            <div key={k} className="stat-box" style={{ opacity: .5, minHeight: 70 }}>
              <div style={{ width: '60%', height: 12, background: 'var(--border)', borderRadius: 4, marginBottom: 8 }} />
              <div style={{ width: '40%', height: 20, background: 'var(--border)', borderRadius: 4 }} />
            </div>
          ))}
        </div>
      )}
      {!quotesLoading && liveData && (hasUSD || hasILS) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div className="stat-box accent-blue">
            <div className="stat-icon">📊</div>
            <div className="stat-label">שינוי יומי</div>
            {hasUSD && (
              <div className="stat-row">
                <span className="stat-row-label">$</span>
                <span className={`stat-value ${liveData.totalDailyChangeUSD >= 0 ? 'green' : 'red'}`} style={{fontSize:'.9rem'}}>
                  {liveData.totalDailyChangeUSD >= 0 ? '+' : ''}{fmt(liveData.totalDailyChangeUSD, 'USD')}
                  {liveData.totalValueUSD > 0 && (
                    <span style={{fontSize:'.72rem', marginRight:4, opacity:.7}}>
                      ({liveData.totalDailyChangeUSD >= 0 ? '+' : ''}{((liveData.totalDailyChangeUSD / (liveData.totalValueUSD - liveData.totalDailyChangeUSD)) * 100).toFixed(2)}%)
                    </span>
                  )}
                </span>
              </div>
            )}
            {hasILS && (
              <div className="stat-row">
                <span className="stat-row-label">₪</span>
                <span className={`stat-value ${liveData.totalDailyChangeILS >= 0 ? 'green' : 'red'}`} style={{fontSize:'.9rem'}}>
                  {liveData.totalDailyChangeILS >= 0 ? '+' : ''}{fmt(liveData.totalDailyChangeILS, 'ILS')}
                </span>
              </div>
            )}
          </div>
          <div className="stat-box accent-green">
            <div className="stat-icon">💹</div>
            <div className="stat-label">רווח/הפסד לא ממומש</div>
            {hasUSD && (
              <div className="stat-row">
                <span className="stat-row-label">$</span>
                <span className={`stat-value ${liveData.totalUnrealizedUSD >= 0 ? 'green' : 'red'}`} style={{fontSize:'.9rem'}}>
                  {liveData.totalUnrealizedUSD >= 0 ? '+' : ''}{fmt(liveData.totalUnrealizedUSD, 'USD')}
                </span>
              </div>
            )}
            {hasILS && (
              <div className="stat-row">
                <span className="stat-row-label">₪</span>
                <span className={`stat-value ${liveData.totalUnrealizedILS >= 0 ? 'green' : 'red'}`} style={{fontSize:'.9rem'}}>
                  {liveData.totalUnrealizedILS >= 0 ? '+' : ''}{fmt(liveData.totalUnrealizedILS, 'ILS')}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      {!quotesLoading && !liveData && (
        <div style={{ color: 'var(--muted)', fontSize: '.82rem' }}>אין נתוני שוק זמינים לפוזיציות הפתוחות</div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState('open');
  const [quotesMap, setQuotesMap] = useState({});
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [usdIls, setUsdIls] = useState(null);
  const captureRef = useRef(null);
  const navigate = useNavigate();

  const handleScreenshot = async () => {
    const prevFilter = filter;
    setCapturing(true);
    if (filter !== 'open') setFilter('open');
    // wait for React to re-render the open-positions list before capturing
    await new Promise(r => setTimeout(r, 400));
    try {
      const node = captureRef.current;
      if (!node) return;
      const bg = getComputedStyle(document.body).backgroundColor || '#f7f8fc';
      // Mobile browsers cap canvas dimensions/area. A tall dashboard at a high scale
      // overflows that cap and gets silently downscaled -> blur. Compute the highest
      // scale that still fits within safe limits so the capture stays crisp.
      const w = node.scrollWidth, h = node.scrollHeight;
      const MAX_DIM = 12000;        // max width/height in px
      const MAX_AREA = 22_000_000;  // max total px (conservative for mobile)
      const desired = (window.devicePixelRatio || 1) * 2;
      const scaleByDim = MAX_DIM / Math.max(w, h);
      const scaleByArea = Math.sqrt(MAX_AREA / (w * h));
      const scale = Math.max(2, Math.min(desired, scaleByDim, scaleByArea, 4));
      const canvas = await html2canvas(node, {
        backgroundColor: bg,
        scale,
        useCORS: true,
        windowWidth: w,
        ignoreElements: (el) => el.hasAttribute && el.hasAttribute('data-screenshot-hide'),
      });
      const blob = await new Promise(res => canvas.toBlob(res, 'image/png'));
      const fileName = `invest-${new Date().toISOString().slice(0, 10)}.png`;
      const file = new File([blob], fileName, { type: 'image/png' });
      // Prefer native share sheet (mobile → WhatsApp directly); fall back to download
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try { await navigator.share({ files: [file], title: 'דשבורד השקעות' }); }
        catch (e) { if (e.name !== 'AbortError') throw e; }
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = fileName;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      console.error('screenshot failed:', e);
      alert('שגיאה ביצירת צילום המסך');
    } finally {
      setCapturing(false);
      if (prevFilter !== 'open') setFilter(prevFilter);
    }
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try { const { data } = await api.get('/instances'); setInstances(data); }
    catch (e) { setError(e.response?.data?.error || 'טעינת הפוזיציות נכשלה'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    let cancelled = false;
    api.get('/fx/usdils').then(r => { if (!cancelled) setUsdIls(r.data?.rate ?? null); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // טעינת שערים חיים — אחרי הדף, לא חוסם את הטעינה הראשית
  useEffect(() => {
    const openWithTicker = instances.filter(i => i.status === 'open' && i.ticker_symbol && i.pnl?.remainingQty > 0);
    if (!openWithTicker.length) return;
    let cancelled = false;
    setQuotesLoading(true);
    Promise.allSettled(
      openWithTicker.map(i => api.get(`/instances/${i.id}/quote`).then(r => [i.id, r.data]).catch(() => [i.id, null]))
    ).then(results => {
      if (cancelled) return;
      const map = {};
      results.forEach(r => { if (r.status === 'fulfilled' && r.value[1]) map[r.value[0]] = r.value[1]; });
      setQuotesMap(map);
      setQuotesLoading(false);
    });
    return () => { cancelled = true; };
  }, [instances]);

  const filtered = instances.filter(i => filter === 'all' || i.status === filter);
  const open = instances.filter(i => i.status === 'open');
  const closed = instances.filter(i => i.status === 'closed');

  const closedILS = closed.filter(i => i.base_currency === 'ILS');
  const closedUSD = closed.filter(i => i.base_currency === 'USD');

  const profitILS = closedILS.reduce((s, i) => s + (i.pnl?.netProfit || 0), 0);
  const profitUSD = closedUSD.reduce((s, i) => s + (i.pnl?.netProfit || 0), 0);

  // Gains vs losses breakdown (net profit per closed position, split by sign)
  const gainsOf = (arr) => arr.reduce((s, i) => { const p = i.pnl?.netProfit || 0; return p > 0 ? s + p : s; }, 0);
  const lossesOf = (arr) => arr.reduce((s, i) => { const p = i.pnl?.netProfit || 0; return p < 0 ? s + p : s; }, 0);
  const gainsILS = gainsOf(closedILS), lossesILS = lossesOf(closedILS);
  const gainsUSD = gainsOf(closedUSD), lossesUSD = lossesOf(closedUSD);

  const insideILS = open.filter(i => i.base_currency === 'ILS').reduce((s, i) => s + Math.max(0, (i.pnl?.totalBoughtCost || 0) - (i.pnl?.totalSoldRevenue || 0)), 0);
  const insideUSD = open.filter(i => i.base_currency === 'USD').reduce((s, i) => s + Math.max(0, (i.pnl?.totalBoughtCost || 0) - (i.pnl?.totalSoldRevenue || 0)), 0);

  // Capital-weighted ROI on closed positions (big positions count proportionally
  // to the capital invested). Everything is converted to shekels via the live
  // USD/ILS rate so ₪ and $ combine into a single figure.
  // weighted ROI = Σ profit(₪) / Σ cost(₪) × 100
  const fx = usdIls || null;
  const toIls = (v, ccy) => ccy === 'ILS' ? v : (fx ? v * fx : null);
  const weightedRoiCombined = (arr) => {
    if (!fx) return null; // need FX to combine; show nothing until rate loads
    let cost = 0, net = 0, gross = 0;
    for (const i of arr) {
      const ccy = i.base_currency;
      cost  += toIls(i.pnl?.totalBoughtCost || 0, ccy);
      net   += toIls(i.pnl?.netProfit       || 0, ccy);
      gross += toIls(i.pnl?.grossProfit     || 0, ccy);
    }
    if (!(cost > 0)) return null;
    return { cost, grossPct: (gross / cost) * 100, netPct: (net / cost) * 100 };
  };
  const wRoi = weightedRoiCombined(closed);

  return (
    <div className="page" ref={captureRef}>
      <div className="page-header">
        <h1 className="page-title">דשבורד</h1>
        <div style={{ display: 'flex', gap: 8 }} data-screenshot-hide>
          <button className="btn btn-secondary btn-sm" onClick={handleScreenshot} disabled={capturing}>
            {capturing ? '📸 מצלם...' : '📸 שתף'}
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>+ חדש</button>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-box accent-blue">
          <div className="stat-icon">📂</div>
          <div className="stat-label">פוזיציות</div>
          <div className="stat-row">
            <span className="stat-row-label">פתוחות</span>
            <span className="stat-value accent">{open.length}</span>
          </div>
          <div className="stat-row">
            <span className="stat-row-label">סגורות</span>
            <span className="stat-value" style={{fontSize:'1.1rem'}}>{closed.length}</span>
          </div>
        </div>
        <div className="stat-box accent-orange">
          <div className="stat-icon">💰</div>
          <div className="stat-label">סה"כ בפנים</div>
          {insideILS === 0 && insideUSD === 0 ? (
            <div className="stat-value" style={{color:'var(--muted)', textAlign:'left'}}>-</div>
          ) : (<>
            {insideILS > 0 && <div className="stat-row">
              <span className="stat-row-label">ש"ח</span>
              <span className="stat-value" style={{fontSize:'.95rem'}}>{fmt(insideILS, 'ILS')}</span>
            </div>}
            {insideUSD > 0 && <div className="stat-row">
              <span className="stat-row-label">דולר</span>
              <span className="stat-value" style={{fontSize:'.95rem'}}>{fmt(insideUSD, 'USD')}</span>
            </div>}
          </>)}
        </div>
        <div className="stat-box accent-green">
          <div className="stat-icon">📈</div>
          <div className="stat-label">רווח/הפסד (סגורות)</div>
          {closed.length === 0 ? (
            <div className="stat-value" style={{color:'var(--muted)', textAlign:'left'}}>-</div>
          ) : (() => {
            const cols = [
              { cur: 'ILS', arr: closedILS, gains: gainsILS, losses: lossesILS, net: profitILS, label: 'ש"ח' },
              { cur: 'USD', arr: closedUSD, gains: gainsUSD, losses: lossesUSD, net: profitUSD, label: 'דולר' },
            ].filter(c => c.arr.length > 0);
            const multi = cols.length > 1;
            return (
              <table className="pnl-mini-table" style={{ width: '100%', marginTop: 2 }}>
                {multi && (
                  <thead>
                    <tr>
                      <th></th>
                      {cols.map(c => <th key={c.cur} style={{ textAlign: 'left', fontSize: '.66rem', fontWeight: 700, color: 'var(--muted)', paddingBottom: 2 }}>{c.label}</th>)}
                    </tr>
                  </thead>
                )}
                <tbody>
                  <tr>
                    <td style={{ color: 'var(--muted)' }}>רווח</td>
                    {cols.map(c => <td key={c.cur} className="green" style={{ textAlign: 'left', fontWeight: 700, whiteSpace: 'nowrap' }}>{c.gains > 0 ? '+' : ''}{fmt(c.gains, c.cur)}</td>)}
                  </tr>
                  <tr>
                    <td style={{ color: 'var(--muted)' }}>הפסד</td>
                    {cols.map(c => <td key={c.cur} className="red" style={{ textAlign: 'left', fontWeight: 700, whiteSpace: 'nowrap' }}>{fmt(c.losses, c.cur)}</td>)}
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 700, borderTop: '1px solid var(--border)', paddingTop: 2 }}>סה"כ</td>
                    {cols.map(c => <td key={c.cur} className={c.net >= 0 ? 'green' : 'red'} style={{ textAlign: 'left', fontWeight: 800, borderTop: '1px solid var(--border)', paddingTop: 2, whiteSpace: 'nowrap' }}>{fmt(c.net, c.cur)}</td>)}
                  </tr>
                </tbody>
              </table>
            );
          })()}
        </div>
        <div className="stat-box accent-purple">
          <div className="stat-icon">🎯</div>
          <div className="stat-label">תשואה משוקללת (סגורות)</div>
          {!wRoi && (
            <div className="stat-row"><span className="stat-row-label" style={{opacity:.6}}>{closed.length ? 'טוען שער…' : 'אין פוזיציות סגורות'}</span></div>
          )}
          {wRoi && (<>
            <div className="stat-row">
              <span className="stat-row-label">ברוטו</span>
              <span className={`stat-value ${wRoi.grossPct >= 0 ? 'green' : 'red'}`} style={{fontSize:'1.1rem'}}>{fmtPct(wRoi.grossPct)}</span>
            </div>
            <div className="stat-row">
              <span className="stat-row-label">נטו</span>
              <span className={`stat-value ${wRoi.netPct >= 0 ? 'green' : 'red'}`} style={{fontSize:'1.1rem'}}>{fmtPct(wRoi.netPct)}</span>
            </div>
            {fx && <div className="stat-row-label" style={{fontSize:'.62rem', opacity:.5, marginTop:3}}>{`מאוחד לפי $1=₪${fx.toFixed(2)}`}</div>}
          </>)}
        </div>
      </div>

      <LivePortfolioSummary instances={instances} quotesMap={quotesMap} quotesLoading={quotesLoading} />

      <div className="tabs">
        {[['open','פתוחות'], ['closed','סגורות'], ['all','הכל']].map(([f, label]) => (
          <button key={f} className={`tab ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>
            {label}
            {f !== 'all' && <span className="tab-count">{instances.filter(i => i.status === f).length}</span>}
          </button>
        ))}
      </div>

      {error && !loading && (
        <div className="error-msg" style={{ marginBottom: 12 }}>
          {error}
          <button className="btn btn-secondary btn-sm" style={{ marginRight: 8 }} onClick={load}>נסה שוב</button>
        </div>
      )}

      {loading ? (
        <div className="loading">טוען...</div>
      ) : filtered.length === 0 ? (
        <div className="empty">
          <div className="empty-icon">📊</div>
          <div className="empty-title">אין פוזיציות {filter === 'open' ? 'פתוחות' : filter === 'closed' ? 'סגורות' : ''}</div>
          <div className="empty-sub">לחץ כדי לפתוח פוזיציה חדשה</div>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ פוזיציה חדשה</button>
        </div>
      ) : (
        filtered.map(inst => {
          const pnl = inst.pnl || {};
          const profit = pnl.netProfit || 0;
          const quote = inst.ticker_symbol ? quotesMap[inst.id] : null;
          const qty = pnl.remainingQty || 0;
          const avgBuy = pnl.avgBuyPrice || 0;

          // חישובים חיים לכרטיס
          // quote prices in agorot for ILS; convert to shekels for monetary display
          const ilsFactor = inst.base_currency === 'ILS' ? 100 : 1;
          const liveUnrealized = quote && qty ? (quote.current - avgBuy) * qty / ilsFactor : null;
          const liveUnrealizedPct = quote && avgBuy ? ((quote.current - avgBuy) / avgBuy) * 100 : null;
          const liveDailyChange = quote && qty && quote.change != null ? quote.change * qty / ilsFactor : null;
          const liveDailyPct = quote?.change_percent_leveraged ?? quote?.change_percent ?? null;

          return (
            <div key={inst.id} className="instance-card" onClick={() => navigate(`/instances/${inst.id}`)}>
              <div className="instance-card-header">
                <div>
                  <div className="stock-symbol">
                    {inst.stock_symbol}
                    {inst.ticker_symbol && inst.ticker_symbol !== inst.stock_symbol && (
                      <span style={{ fontSize: '.7rem', fontWeight: 600, color: 'var(--muted)', marginRight: 6, background: 'var(--surface2)', borderRadius: 4, padding: '1px 6px', verticalAlign: 'middle' }}>
                        {inst.ticker_symbol}
                      </span>
                    )}
                  </div>
                  <div className="platform">{inst.platform_name} · {inst.base_currency}</div>
                </div>
                <span className={`badge ${inst.status}`}>{inst.status === 'open' ? 'פתוחה' : 'סגורה'}</span>
              </div>
              <div className="stats-grid" style={{marginBottom: 10}}>
                <div className="stat-box">
                  <div className="stat-label">רווח נקי</div>
                  <div className={`stat-value ${profit >= 0 ? 'green' : 'red'}`}>{fmt(profit, inst.base_currency)}</div>
                  {liveUnrealized !== null && (
                    <div style={{marginTop:4, fontSize:'.72rem', fontWeight:600, color: liveUnrealized >= 0 ? 'var(--green)' : 'var(--red)'}}>
                      עכשיו: {fmtLive(liveUnrealized, inst.base_currency)}
                      {liveUnrealizedPct !== null && <span style={{opacity:.75}}> ({liveUnrealizedPct >= 0 ? '+' : ''}{liveUnrealizedPct.toFixed(2)}%)</span>}
                    </div>
                  )}
                  {inst.status === 'open' && inst.ticker_symbol && !quote && quotesLoading && (
                    <div style={{marginTop:4, fontSize:'.68rem', color:'var(--muted)'}}>טוען שוק...</div>
                  )}
                </div>
                <div className="stat-box">
                  <div className="stat-label">תשואה</div>
                  <div className={`stat-value ${(pnl.roi||0) >= 0 ? 'green' : 'red'}`}>{fmtPct(pnl.roi || 0)}</div>
                </div>
                <div className="stat-box">
                  <div className="stat-label">בהחזקה</div>
                  <div className="stat-value" style={{color:'var(--accent)', fontWeight:900}}>{+parseFloat(qty).toFixed(4)}</div>
                </div>
                <div className="stat-box">
                  <div className="stat-label">ממוצע קנייה</div>
                  <div className="stat-value" style={{color:'var(--text2)'}}>{fmtPrice(avgBuy, inst.base_currency)}</div>
                  {liveDailyChange !== null && (() => {
                    const ms = quote?.market_state;
                    const hasPre  = quote?.pre_market_price != null || quote?.pre_market_change_percent != null;
                    // Show pre-market only when a real pre-market print exists; otherwise
                    // fall back to the LAST after-hours price so it stays visible overnight
                    // (PREPRE / CLOSED) even when no extended session is live.
                    const usePre = (ms === 'PRE' || ms === 'PREPRE') && hasPre;
                    const extPrice  = usePre ? quote?.pre_market_price : quote?.post_market_price;
                    const extChgPct = usePre ? quote?.pre_market_change_percent : quote?.post_market_change_percent;
                    const showExt = ms && ms !== 'REGULAR' && (extPrice != null || extChgPct != null);
                    const extLabel  = usePre ? '🌅 טרום' : '🌙 אחמ"ש';
                    return (
                      <>
                        <div style={{marginTop:4, fontSize:'.72rem', fontWeight:600, color: liveDailyChange >= 0 ? 'var(--green)' : 'var(--red)'}}>
                          שינוי יומי: {liveDailyChange >= 0 ? '+' : ''}{fmt(liveDailyChange, inst.base_currency)}
                          {liveDailyPct !== null && <span style={{opacity:.75}}> ({liveDailyPct >= 0 ? '+' : ''}{liveDailyPct.toFixed(2)}%)</span>}
                          {quote?.current != null && <span style={{color:'var(--text2)', fontWeight:700}}> · {fmtPrice(quote.current, inst.base_currency)}</span>}
                        </div>
                        {showExt && (
                          <div style={{marginTop:3, fontSize:'.7rem', fontWeight:700,
                            color: (extChgPct ?? 0) >= 0 ? 'var(--green)' : 'var(--red)',
                            background: 'var(--surface2)', borderRadius: 5, padding: '2px 5px', display:'inline-block'}}>
                            {extLabel}:
                            {extChgPct != null && <span> {extChgPct >= 0 ? '+' : ''}{extChgPct.toFixed(2)}%</span>}
                            {extPrice != null && <span style={{color:'var(--text2)'}}> · {fmtPrice(extPrice, inst.base_currency)}</span>}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
              {inst.status === 'open' && (() => {
                const stillInside = Math.max(0, (pnl.totalBoughtCost || 0) - (pnl.totalSoldRevenue || 0));
                return stillInside > 0 ? (
                  <div style={{
                    background: 'var(--accent-light)',
                    border: '1px solid var(--accent)',
                    borderRadius: 8,
                    padding: '8px 12px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 6
                  }}>
                    <span style={{fontSize: '.78rem', fontWeight: 600, color: 'var(--accent)'}}>💰 עוד בפנים</span>
                    <span style={{fontSize: '.95rem', fontWeight: 800, color: 'var(--accent)'}}>{fmt(stillInside, inst.base_currency)}</span>
                  </div>
                ) : null;
              })()}
              <div style={{ color: 'var(--muted)', fontSize: '.75rem' }}>
                נפתחה {new Date(inst.created_at).toLocaleDateString('he-IL')}
                {inst.notes && ` · ${inst.notes}`}
              </div>
            </div>
          );
        })
      )}

      {showModal && <NewInstanceModal onClose={() => setShowModal(false)} onCreated={() => { setShowModal(false); load(); }} />}
    </div>
  );
}
